import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta } from '@plataforma/web/formato';

interface Ticket {
  id: string;
  folio: string;
  asunto: string;
  categoria: string;
  estado: string;
  creadoEn: string;
  actualizadoEn: string;
  mensajes?: { id: string; autorTipo: string; autor: string; cuerpo: string; creadoEn: string }[];
}

const ESTADO: Record<string, string> = {
  abierto: 'Abierto',
  en_progreso: 'En revisión',
  esperando_cliente: 'Te respondimos',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
};
const CATEGORIAS: [string, string][] = [
  ['general', 'Duda general'],
  ['tecnico', 'Algo no funciona'],
  ['cobro', 'Cobros y suscripción'],
  ['pedido', 'Un pedido'],
  ['otro', 'Otro'],
];

/** Mis tickets de soporte (arquitectura 2.12): los de mi lotero, con su conversación. */
export function MisTickets() {
  const lista = useConsulta<Ticket[]>('/platform/mi-lotero/tickets');
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Soporte</h1>
          <div className="subtitulo">Escríbenos y te respondemos aquí y por correo</div>
        </div>
        <div className="relleno" />
        <Link className="boton primario" to="/ayuda/tickets/nuevo">
          Nuevo ticket
        </Link>
      </div>
      {lista.error && <p className="error">{lista.error}</p>}
      <section className="seccion">
        <table>
          <tbody>
            {(lista.datos ?? []).map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/ayuda/tickets/${t.id}`}>{t.folio}</Link>
                </td>
                <td>{t.asunto}</td>
                <td>
                  <span
                    className={`etiqueta ${t.estado === 'esperando_cliente' ? 'aviso' : t.estado === 'resuelto' ? 'exito' : ''}`}
                  >
                    {ESTADO[t.estado] ?? t.estado}
                  </span>
                </td>
                <td>{fechaCorta(t.actualizadoEn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {lista.datos?.length === 0 && <p className="vacio">Aún no tienes tickets.</p>}
      </section>
    </>
  );
}

export function NuevoTicket() {
  const api = useApi();
  // 2.27: desde «Errores» llega con el asunto y el folio.
  const [consulta] = useSearchParams();
  const navegar = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    setError(null);
    try {
      const t = await api<{ id: string }>('POST', '/platform/mi-lotero/tickets', {
        asunto: String(d.get('asunto') ?? ''),
        categoria: String(d.get('categoria') ?? 'general'),
        mensaje: String(d.get('mensaje') ?? ''),
        ...(d.get('referencia') && { referencia: String(d.get('referencia')) }),
      });
      navegar(`/ayuda/tickets/${t.id}`);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/ayuda/tickets">Soporte</Link> › nuevo
          </div>
          <h1>Nuevo ticket</h1>
        </div>
      </div>
      <form className="seccion cuerpo formulario" onSubmit={(e) => void enviar(e)}>
        <label className="campo">
          <span>Asunto</span>
          <input
            name="asunto"
            required
            minLength={4}
            maxLength={160}
            defaultValue={consulta.get('asunto') ?? ''}
          />
        </label>
        <label className="campo">
          <span>Tema</span>
          <select name="categoria">
            {CATEGORIAS.map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span>Cuéntanos qué pasa</span>
          <textarea name="mensaje" required minLength={10} rows={6} maxLength={10000} />
        </label>
        <label className="campo">
          <span>Referencia (opcional: VIN, pedido, folio)</span>
          <input name="referencia" maxLength={80} defaultValue={consulta.get('referencia') ?? ''} />
        </label>
        {error && <p className="error">{error}</p>}
        <div>
          <button className="boton primario">Enviar</button>
        </div>
      </form>
    </>
  );
}

export function TicketDetalle() {
  const { id } = useParams();
  const api = useApi();
  const t = useConsulta<Ticket>(`/platform/mi-lotero/tickets/${id}`);
  const [error, setError] = useState<string | null>(null);
  const responder = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formulario = e.currentTarget;
    setError(null);
    try {
      await api('POST', `/platform/mi-lotero/tickets/${id}/mensajes`, {
        cuerpo: String(new FormData(formulario).get('cuerpo') ?? ''),
      });
      formulario.reset();
      t.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  if (t.error) return <p className="error">{t.error}</p>;
  if (!t.datos) return <p className="subtitulo">Cargando…</p>;
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/ayuda/tickets">Soporte</Link> › {t.datos.folio}
          </div>
          <h1>{t.datos.asunto}</h1>
        </div>
        <div className="relleno" />
        <span className="etiqueta">{ESTADO[t.datos.estado] ?? t.datos.estado}</span>
      </div>
      <section className="seccion">
        <div className="cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(t.datos.mensajes ?? []).map((m) => (
            <div
              key={m.id}
              style={{ padding: 10, borderRadius: 8, border: '1px solid var(--borde-suave)' }}
            >
              <div className="subtitulo">
                {m.autorTipo === 'agente' ? 'Soporte Siniestrados' : m.autor} ·{' '}
                {fechaCorta(m.creadoEn)}
              </div>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{m.cuerpo}</p>
            </div>
          ))}
        </div>
        {t.datos.estado !== 'cerrado' && (
          <form
            className="cuerpo formulario"
            onSubmit={(e) => void responder(e)}
            style={{ borderTop: '1px solid var(--borde-suave)' }}
          >
            <label className="campo">
              <span>Responder</span>
              <textarea name="cuerpo" required rows={4} maxLength={10000} />
            </label>
            {error && <p className="error">{error}</p>}
            <div>
              <button className="boton primario">Enviar</button>
            </div>
          </form>
        )}
      </section>
    </>
  );
}
